import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiList } from 'react-icons/fi';

interface Badge {
  text: string;
  variant?: string;
  color?: string;
  tooltip?: string;
}

interface ListItem {
  title: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  value?: string;
  timestamp?: string;
  link?: string;
  tooltip?: string;
  badge?: Badge;
}

interface Props {
  items: ListItem[];
  maxItems?: number;
  showBadge?: boolean;
}

const ListWidget: React.FC<Props> = ({ items, maxItems = 5, showBadge = false }) => {
  const navigate = useNavigate();

  if (!items || items.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <FiList className="w-8 h-8 mb-2 mx-auto text-gray-400" />
          <div className="text-sm">No items to display</div>
        </div>
      </div>
    );
  }

  const displayItems = items.slice(0, maxItems);

  const getBadgeColor = (variant: string | undefined): string => {
    const colors: Record<string, string> = {
      success: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
      error: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300',
      warning: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300',
      info: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
      green: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
      red: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300',
      blue: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
      default: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
    };
    return colors[variant ?? 'default'] || colors.default;
  };

  return (
    <div className="space-y-3">
      {displayItems.map((item) => (
        <div
          key={item.title + (item.subtitle ?? '') + (item.timestamp ?? '')}
          onClick={item.link ? () => { if (item.link) navigate(item.link); } : undefined}
          onKeyDown={item.link ? (e) => { if ((e.key === 'Enter' || e.key === ' ') && item.link) { e.preventDefault(); navigate(item.link); } } : undefined}
          role={item.link ? 'button' : undefined}
          tabIndex={item.link ? 0 : undefined}
          className={`flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md transition-colors ${
            item.link
              ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-200 dark:hover:border-blue-800 border border-transparent'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {item.icon && <span className="text-sm">{item.icon}</span>}
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate transition-colors">
                {item.title}
              </p>
            </div>
            {item.subtitle && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 transition-colors">
                {item.subtitle}
              </p>
            )}
            {item.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 transition-colors">
                {item.description}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 ml-3">
            {showBadge && item.badge && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${getBadgeColor(item.badge.variant || item.badge.color)}`}
                title={item.badge.tooltip}
              >
                {item.badge.text}
              </span>
            )}
            {item.value && (
              <span
                className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap transition-colors"
                title={item.tooltip}
              >
                {item.value}
              </span>
            )}
            {item.timestamp && (
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap transition-colors">
                {item.timestamp}
              </span>
            )}
          </div>
        </div>
      ))}
      {items.length > maxItems && (
        <div className="text-xs text-center text-gray-500 dark:text-gray-400 pt-2 transition-colors">
          +{items.length - maxItems} more
        </div>
      )}
    </div>
  );
};

export default ListWidget;
